/**
 * @module Experience/repdash
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

// core libraries
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SETTINGS } from '@environment/settings';
import { Router } from '@angular/router';
// interfaces
import { Line } from '@experience/landing/common/components/account-landing/interfaces/account-lines-device-details.interface';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import {
  Line as pendingOrderResponseLine,
  TotalPendingOrdersResponse,
} from '@support/api/proxies/responses/api-2817.interface';
import { CustomerAccountLineDeviceDetails } from '@support/api/proxies/responses/api-2022.interface';
// Services
import { AccountLandingService } from '@experience/landing/common/components/account-landing/services/account-landing.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { UserService } from '@support/shared/services/user.service';
import { CartNotEmptyModalService } from '@support/shared/services/cart-not-empty-modal.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
// constants
import { ROUTER_CONSTANTS } from '@experience/landing/common/constants/line-details.constants';
import { AccountLineDetailsModel } from '@experience/landing/common/components/account-landing/models/account-line-details.model';
import { KeyValueMap } from '@support/shared/interfaces/key-value-map.interface';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import { ACCOUNT_LANDING_CONSTANTS } from '@experience/landing/common/constants/account-landing.constants';
import { CPS_ROUTES } from '@support/shared/constants/app-routes.constants';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';

@Component({
  selector: 'tmo-account-line-details',
  templateUrl: './account-line-details.component.html',
  styleUrls: ['../../../../../../style-sheets/components/account-line-details.component.scss'],
})
export class AccountLineDetailsComponent implements OnInit, OnDestroy {
  /**
   * stores line details as input from account landing
   */
  @Input() public accountLineDetailsModel: AccountLineDetailsModel;
  /**
   * holds content data received from account landing
   */
  @Input() public contentData: ConfigData;
  /**
   * Holds boolean for tourist plan
   */
  @Input() public isTouristPlan: boolean;
  /**
   * stores primary msisdn
   */
  @Input() public primaryMsisdn: string;
  /**
   * stores primary msisdn
   */
  @Input() public permissionEnabled: string;
  /**
   * stores url to show device image
   */
  public extTmoImagesUrl: string;
  /**
   * stores default image url
   */
  public imageUrl: string;
  /**
   * stores primary constant value
   */
  public primaryConstant: string;
  /**
   * stores constant value of Permission Constant
   */
  public permissionConstants: KeyValueMap;
  /**
   * URL for change plans page
   */
  public changePlansUrl: string;
  /**
   * Flag to check permission
   */
  public isPermissionAvailable: boolean;
  /**
   * flag to check permission
   */
  public isUpgradePermissionAvailable: boolean;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;

  /**
   *  isNewDesignEnabled to check new or old design;
   */
  public isNewDesignEnabled: boolean;
  public pendingOrdersCount: number;
  /**
   * ACC_LANDING_CONSTANTS_OBJECT to utilize the constants object in html
   */
  public readonly ACC_LANDING_CONSTANTS_OBJECT: object = ACCOUNT_LANDING_CONSTANTS;

  @Input() public isP10MCustomer: boolean;

  constructor(
    private accountLandingService: AccountLandingService,
    private commonUtilService: CommonUtilService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private featureToggleUtilService: FeatureToggleUtilService,
    private router: Router,
    private userService: UserService,
    private cartSupportService: CartSupportService,
    private cartNotEmptyModalService: CartNotEmptyModalService
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.extTmoImagesUrl = SETTINGS.EXT_IMAGE_URL;
    this.changePlansUrl = CPS_ROUTES.CHANGE_PLAN;
    this.primaryConstant = ACCOUNT_LANDING_CONSTANTS.PRIMARY.toLowerCase();
    this.isPermissionAvailable = true;
    this.isUpgradePermissionAvailable = true;
  }
  /**
   * Loads the initial data of Account Landing Page
   * @returns void
   */
  public ngOnInit(): void {
    this.permissionConstants = PERMISSION_CONSTANTS;
    this.isPermissionAvailable = this.featureToggleUtilService.getFraudLockPermission(
      this.router.url,
      PERMISSION_CONSTANTS.ORDER_DEVICE
    );
    this.isUpgradePermissionAvailable = this.featureToggleUtilService.getFraudLockPermission(
      this.router.url,
      PERMISSION_CONSTANTS.PRE_ORDER_DEVICE
    );
    const index: number = 0;
    this.getTotalPendingOrderCount();
    this.getDeviceDetails(this.stateStoreUtilsService.getMsisdn(), index);
    this.imageUrl = this.commonUtilService.getImageUrl('');
    this.isNewDesignEnabled = true;
  }
  /**
   * Method to set device details on every line blade click
   * @param {string} msisdn
   * @param {number} index
   * @returns void
   */
  public getDeviceDetails(msisdn: string, index: number): void {
    this.accountLandingService
      .getCustomerLineDeviceDetails(msisdn)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((customerAccountLineDeviceDetails: CustomerAccountLineDeviceDetails) => {
        this.accountLineDetailsModel.getDeviceDetails(customerAccountLineDeviceDetails, index);
      });
  }
  /**
   * Method to fetch device details from API 2022 on every line blade click
   * @param {string} msisdn
   * @param {number} index
   * @returns void
   */
  public onLineBladeClick(msisdn: string, index: number, lineOfServiceId?: string): void {
    this.getDeviceDetails(msisdn, index);
  }
  /**
   * Method to set expand/collapse property of line blades
   * @param {number} index
   * @param {boolean} active
   * @returns void
   */
  public updateActiveBladeIndex(bladeIndex: number, active: boolean): void {
    this.accountLineDetailsModel.updateActiveBladeIndex(bladeIndex, active);
  }
  /**
   * Method to fetch pending orders from API 2813 for line blade clicked
   * @param {string} msisdn
   * @param {number} index
   * @returns void
   */
  public getTotalPendingOrderCount(): void {
    this.accountLandingService
      .getPendingOrderCount()
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((totalPendingOrdersResponse: TotalPendingOrdersResponse) => {
        if (totalPendingOrdersResponse.lines) {
          this.pendingOrdersCount = totalPendingOrdersResponse.totalPendingOrderCount;
          let recordIndex: number = 0;
          this.accountLineDetailsModel.customerAccountLineDetails.forEach((line: Line) => {
            const lineServiceId = line.lineOfServiceId;
            this.accountLineDetailsModel.customerAccountLineDetails[recordIndex].pendingOrders =
              this.commonUtilService.messageFormatter(this.contentData.pendingOrders, ['0']);
            totalPendingOrdersResponse.lines.forEach(
              (customerAccountLine: pendingOrderResponseLine) => {
                if (customerAccountLine.lineOfServiceId === lineServiceId) {
                  this.accountLineDetailsModel.customerAccountLineDetails[
                    recordIndex
                  ].pendingOrders = this.commonUtilService.messageFormatter(
                    this.contentData.pendingOrders,
                    [customerAccountLine.pendingCount]
                  );
                }
              }
            );
            recordIndex++;
          });
        }
      });
  }

  /**
   * Method to navigate to line landing and set current line details in state store
   * @param {Line} lineDetails
   * @returns void
   */
  public onLineDetailsClick(lineDetails: Line): void {
    this.stateStoreUtilsService.setMsisdn(lineDetails.msisdn);
    this.stateStoreUtilsService.setLineId(lineDetails.lineOfServiceId);
    this.router.navigate([ROUTER_CONSTANTS.LINE_LANDING]);
  }

  /**
   * Method to navigate to CPS (Change Plans and Services)flow
   * @returns void
   */
  public navigateToCPS(lineDetails: Line): void {
    this.stateStoreUtilsService.setCPSEntryPoint(this.router.url);
    this.stateStoreUtilsService.setMsisdn(lineDetails.msisdn);
    this.stateStoreUtilsService.setLineId(lineDetails.lineOfServiceId);
    this.router.navigate([CPS_ROUTES.CHANGE_PLAN]);
  }

  /**
   * Method to Navigate to Line Upgrade Flow
   * @param lineId string value of selected line id
   * @returns void
   */
  public navigateToLineUpgrade(lineId?: string): void {
    const cartData = this.stateStoreUtilsService.getCart();
    this.userService
      .getCustomerBillingDetailsData(this.stateStoreUtilsService.getLineId())
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(() => {
        this.stateStoreUtilsService.setSelectedUpgradeLineId(lineId);
        if (this.cartSupportService.isCartNotEmpty(cartData)) {
          const route = {
            navigateToRoute: ACCOUNT_LANDING_CONSTANTS.BROWSE_DEVICES,
            queryParams: { isUpgrade: true },
          };
          this.cartNotEmptyModalService.openModal(route);
        } else {
          // if (this.commonUtilService.isRetailChannel()) {
          //   this.stateStoreUtilsService.setUpgradeDevice(true);
          //   this.router.navigate(['plan-services']);
          // } else {
          this.router.navigate([ACCOUNT_LANDING_CONSTANTS.BROWSE_DEVICES], {
            queryParams: { isUpgrade: true },
          });
          // }
        }
      });
  }
  /**
   * function to set line sub status
   * @param {Line} lineDetails
   * @returns {string}
   * @memberof AccountLineDetailsComponent
   */
  public getSubStatus(lineDetails: Line): string {
    return this.userService.getLineStatus(lineDetails);
  }

  /**
   * Unsubscribes for all subscriptions
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
